import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, apiUpload, currentAccessToken } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, ErrorBox } from '../components/common';
import { config } from '../config';
import { formatDate } from '../labels';
import { useReferentials } from './Workspace';
import '../styles/archives.css';

const FIELDS: { key: string; label: string; hint?: string }[] = [
  { key: 'surname', label: 'Nom' },
  { key: 'givenNames', label: 'Prénoms' },
  { key: 'birthDate', label: 'Naissance', hint: 'AAAA, AAAA-MM ou AAAA-MM-JJ' },
  { key: 'birthPlace', label: 'Lieu de naissance' },
  { key: 'decisionReference', label: 'Référence de la décision' },
  { key: 'decisionDate', label: 'Date de la décision', hint: 'AAAA-MM-JJ' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'sentence', label: 'Peine' },
];

export const ITEM_STATUS: Record<string, { label: string; tone: string }> = {
  capture: { label: 'Capturée', tone: 'muted' },
  a_verifier: { label: 'À vérifier', tone: 'info' },
  double_controle: { label: 'Double contrôle', tone: 'warn' },
  soumis: { label: 'À valider', tone: 'info' },
  valide: { label: 'Validée', tone: 'ok' },
  rejete: { label: 'Rejetée', tone: 'danger' },
  integre: { label: 'Intégrée au registre', tone: 'ok' },
  consultable: { label: 'Archive consultable', tone: 'muted' },
};

const STATE: Record<string, string> = {
  bon: 'Bon',
  fragile: 'Fragile',
  degrade: 'Dégradé',
  tres_degrade: 'Très dégradé',
};

function InfoIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v4h1" />
    </svg>
  );
}

/** Barre d'avancement décorative : le texte qui l'accompagne porte l'information. */
function ProgressBar({ value, total }: { value: number; total: number | null }) {
  if (!total) return null;
  const pct = Math.max(0, Math.min(100, Math.round((value / total) * 100)));
  return (
    <span className="arch-bar" aria-hidden="true">
      <span style={{ width: `${pct}%` }} />
    </span>
  );
}

interface Batch {
  id: string;
  code: string;
  jurisdiction: string;
  series: string;
  year_from: number | null;
  year_to: number | null;
  declared_pages: number | null;
  physical_state: string;
  priority: number;
  status: string;
  captured_pages: number;
  done_items: number;
  items: number;
}

export function ArchivesPage() {
  const { me } = useAuth();
  const refs = useReferentials();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Batch[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [form, setForm] = useState({
    code: '',
    jurisdictionId: '',
    holder: '',
    series: '',
    yearFrom: '',
    yearTo: '',
    declaredPages: '',
    physicalState: 'bon',
    priority: '2',
    location: '',
    knownGaps: '',
  });
  const canCapture = me?.permissions.includes('archives.capturer') ?? false;
  useEffect(() => {
    api<Batch[]>('/archives/lots').then(setRows).catch(setError);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const num = (v: string) => (v ? Number(v) : undefined);
    try {
      const b = await api<{ id: string }>('/archives/lots', {
        method: 'POST',
        body: {
          code: form.code.toUpperCase(),
          jurisdictionId: form.jurisdictionId,
          holder: form.holder,
          series: form.series,
          yearFrom: num(form.yearFrom),
          yearTo: num(form.yearTo),
          declaredPages: num(form.declaredPages),
          physicalState: form.physicalState,
          priority: Number(form.priority),
          location: form.location,
          knownGaps: form.knownGaps || undefined,
        },
      });
      navigate(`/espace/archives/lots/${b.id}`);
    } catch (err) {
      setError(err as ApiError);
    }
  };

  return (
    <div className="stack">
      <div className="head">
        <div>
          <h1>Archives</h1>
          <p className="lead">
            Inventaire, capture, vérification et validation. Une extraction reste une proposition tant que la
            validation n’est pas achevée ; la numérisation n’autorise aucune destruction d’original.
          </p>
        </div>
      </div>
      <ErrorBox error={error} />
      <section className="card np arch-table" aria-labelledby="arch-lots">
        <div className="arch-section-title">
          <h2 id="arch-lots">Lots</h2>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Lot</th>
                <th scope="col">Fonds</th>
                <th scope="col">État</th>
                <th scope="col">Avancement</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    Aucun lot inventorié.
                  </td>
                </tr>
              )}
              {rows.map((b) => (
                <tr key={b.id}>
                  <td>
                    <span className="arch-cell">
                      <Link to={`/espace/archives/lots/${b.id}`} className="mono">
                        {b.code}
                      </Link>
                      <small>Priorité {b.priority}</small>
                    </span>
                  </td>
                  <td data-l="Fonds">
                    <span className="arch-cell">
                      <span>
                        {b.series} - {b.jurisdiction}
                      </span>
                      <small>
                        {b.year_from ?? '?'}-{b.year_to ?? '?'}, état {STATE[b.physical_state]?.toLowerCase()}
                      </small>
                    </span>
                  </td>
                  <td data-l="État">
                    <Badge tone={b.status === 'cloture' ? 'ok' : 'info'}>
                      {b.status === 'cloture'
                        ? 'Clôturé'
                        : b.status === 'inventorie'
                          ? 'Inventorié'
                          : 'En cours'}
                    </Badge>
                  </td>
                  <td data-l="Avancement">
                    <span className="arch-progress">
                      <ProgressBar value={b.captured_pages} total={b.declared_pages} />
                      <small className="arch-sub">
                        {b.captured_pages} page(s) capturée(s)
                        {b.declared_pages ? ` / ${b.declared_pages} déclarées` : ''} - {b.done_items}/
                        {b.items} pièce(s) traitée(s)
                      </small>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {canCapture && (
        <details className="acc" open>
          <summary>
            <span>
              Inventorier un lot
              <small>Déclarer un nouveau registre avant sa capture</small>
            </span>
          </summary>
          <div className="body">
            <form className="stack" onSubmit={submit} aria-label="Inventorier un lot">
              <div className="fg">
                <div className="field">
                  <label htmlFor="b-code">Code</label>
                  <input
                    id="b-code"
                    required
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="LBV-REG-1987-03"
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-j">Juridiction</label>
                  <select
                    id="b-j"
                    required
                    value={form.jurisdictionId}
                    onChange={(e) => setForm({ ...form, jurisdictionId: e.target.value })}
                  >
                    <option value="">- Choisir -</option>
                    {(refs?.jurisdictions ?? []).map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="b-h">Détenteur</label>
                  <input
                    id="b-h"
                    required
                    value={form.holder}
                    onChange={(e) => setForm({ ...form, holder: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-s">Série</label>
                  <input
                    id="b-s"
                    required
                    value={form.series}
                    onChange={(e) => setForm({ ...form, series: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-y1">Année début</label>
                  <input
                    id="b-y1"
                    inputMode="numeric"
                    value={form.yearFrom}
                    onChange={(e) => setForm({ ...form, yearFrom: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-y2">Année fin</label>
                  <input
                    id="b-y2"
                    inputMode="numeric"
                    value={form.yearTo}
                    onChange={(e) => setForm({ ...form, yearTo: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-p">Pages déclarées</label>
                  <input
                    id="b-p"
                    inputMode="numeric"
                    value={form.declaredPages}
                    onChange={(e) => setForm({ ...form, declaredPages: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-st">État physique</label>
                  <select
                    id="b-st"
                    value={form.physicalState}
                    onChange={(e) => setForm({ ...form, physicalState: e.target.value })}
                  >
                    {Object.entries(STATE).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="b-pr">Priorité</label>
                  <select
                    id="b-pr"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  >
                    <option value="1">1 - haute</option>
                    <option value="2">2</option>
                    <option value="3">3 - basse</option>
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="b-l">Localisation physique</label>
                  <input
                    id="b-l"
                    required
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                  />
                </div>
                <div className="field full">
                  <label htmlFor="b-g">Lacunes connues</label>
                  <textarea
                    id="b-g"
                    value={form.knownGaps}
                    onChange={(e) => setForm({ ...form, knownGaps: e.target.value })}
                  />
                </div>
              </div>
              <div className="actions arch-actions">
                <button className="btn" type="submit">
                  Inventorier
                </button>
              </div>
            </form>
          </div>
        </details>
      )}
    </div>
  );
}

interface BatchDetail extends Batch {
  holder: string;
  location: string;
  known_gaps: string | null;
  items: {
    id: string;
    sequence: number;
    page_from: number;
    page_to: number;
    status: string;
    quality: Record<string, string>;
    captured_at: string;
  }[] &
    number;
  stats: {
    declaredPages: number | null;
    capturedPages: number;
    missingPages: number[];
    duplicatedPages: number[];
    illegible: number;
    returnedForCorrection: number;
    extractionCorrectionRate: number | null;
  };
  notice: string;
}

export function BatchPage() {
  const { id } = useParams();
  const { me } = useAuth();
  const [b, setB] = useState<BatchDetail | null>(null);
  const [pages, setPages] = useState({ from: '1', to: '1' });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const load = useCallback(() => api<BatchDetail>(`/archives/lots/${id}`).then(setB).catch(setError), [id]);
  useEffect(() => {
    void load();
  }, [load]);
  const canCapture = me?.permissions.includes('archives.capturer') ?? false;

  const upload = async () => {
    if (!file) return;
    setError(null);
    const form = new FormData();
    form.append('pageFrom', pages.from);
    form.append('pageTo', pages.to);
    form.append('fichier', file);
    try {
      await apiUpload(`/archives/lots/${id}/pieces`, form);
      setFile(null);
      setPages({ from: String(Number(pages.to) + 1), to: String(Number(pages.to) + 1) });
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };

  if (!b) return error ? <ErrorBox error={error} /> : <p>Chargement…</p>;
  const items = b.items as unknown as BatchDetail['items'];
  return (
    <div className="stack">
      <div className="head">
        <div>
          <p className="crumb">
            <Link to="/espace/archives">‹ Archives</Link>
          </p>
          <h1 style={{ overflowWrap: 'anywhere' }}>
            Lot <span className="mono">{b.code}</span>
          </h1>
        </div>
      </div>
      <ErrorBox error={error} />
      <div className="kpis">
        <div className="stat">
          <span className="v">{b.stats.capturedPages}</span>
          <span className="l">
            page(s) capturée(s){b.stats.declaredPages ? ` sur ${b.stats.declaredPages} déclarées` : ''}
          </span>
        </div>
        <div className="stat">
          <span className="v">{b.stats.missingPages.length}</span>
          <span className="l">
            page(s) manquante(s)
            {b.stats.duplicatedPages.length ? `, ${b.stats.duplicatedPages.length} en double` : ''}
          </span>
        </div>
        <div className="stat">
          <span className="v">{b.stats.illegible}</span>
          <span className="l">pièce(s) à lisibilité douteuse</span>
        </div>
        <div className="stat">
          <span className="v">
            {b.stats.extractionCorrectionRate === null ? '-' : `${b.stats.extractionCorrectionRate} %`}
          </span>
          <span className="l">
            des valeurs proposées corrigées ; {b.stats.returnedForCorrection} retour(s) en correction
          </span>
        </div>
      </div>
      <p className="alert alert-ic">
        <InfoIcon />
        <span>{b.notice}</span>
      </p>
      {b.stats.missingPages.length > 0 && (
        <p className="alert alert-warn" style={{ overflowWrap: 'anywhere' }}>
          Pages manquantes : {b.stats.missingPages.slice(0, 40).join(', ')}
          {b.stats.missingPages.length > 40 ? '…' : ''}
        </p>
      )}
      <section className="card np arch-table" aria-labelledby="arch-pieces">
        <div className="arch-section-title">
          <h2 id="arch-pieces">Pièces</h2>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Pièce</th>
                <th scope="col">Pages</th>
                <th scope="col">Contrôle d’image</th>
                <th scope="col">Statut</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id}>
                  <td>
                    <Link to={`/espace/archives/pieces/${i.id}`}>n° {i.sequence}</Link>
                  </td>
                  <td data-l="Pages">
                    <span>
                      {i.page_from}
                      {i.page_to !== i.page_from ? `-${i.page_to}` : ''}
                    </span>
                  </td>
                  <td data-l="Contrôle d’image">
                    {i.quality.lisibilite === 'douteuse' ? (
                      <Badge tone="warn">Lisibilité douteuse</Badge>
                    ) : (
                      <span>Conforme</span>
                    )}
                  </td>
                  <td data-l="Statut">
                    <Badge tone={ITEM_STATUS[i.status]?.tone ?? 'muted'}>
                      {ITEM_STATUS[i.status]?.label ?? i.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {canCapture && b.status !== 'cloture' && (
        <div className="card flow">
          <h2>Capturer une pièce</h2>
          <div className="fg">
            <div className="field">
              <label htmlFor="c-from">Page de début</label>
              <input
                id="c-from"
                inputMode="numeric"
                value={pages.from}
                onChange={(e) => setPages({ ...pages, from: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="c-to">Page de fin</label>
              <input
                id="c-to"
                inputMode="numeric"
                value={pages.to}
                onChange={(e) => setPages({ ...pages, to: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="c-file">Numérisation (PDF, JPEG, PNG)</label>
              <input
                id="c-file"
                className="arch-file"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="field end">
              <button type="button" className="btn" disabled={!file} onClick={() => void upload()}>
                Capturer
              </button>
            </div>
          </div>
          <div className="actions arch-actions arch-sep">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() =>
                void api(`/archives/lots/${id}/cloturer`, { method: 'POST' })
                  .then(() => load())
                  .catch(setError)
              }
            >
              Clôturer le lot
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface Item {
  id: string;
  batch_id: string;
  batch_code: string;
  sequence: number;
  page_from: number;
  page_to: number;
  status: string;
  mime: string;
  original_sha256: string;
  capture_device: string;
  extraction_model: string | null;
  extraction: Record<string, { value: string | null; confidence: number }> | null;
  verified: Record<string, string | null> | null;
  verified_by: string | null;
  outcome: string | null;
  inscription_id: string | null;
  decision_reason: string | null;
  uncertainFields: string[];
  threshold: number;
  history: { to_status: string; reason: string | null; occurred_at: string; actor: string }[];
}

/** Vérification côte à côte : original et valeurs proposées, zones incertaines signalées (§8.5). */
export function ArchiveItemPage() {
  const { id } = useParams();
  const { me } = useAuth();
  const [item, setItem] = useState<Item | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<'inscription' | 'consultable'>('inscription');
  const [reason, setReason] = useState('');
  const [personId, setPersonId] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const can = (p: string) => me?.permissions.includes(p) ?? false;

  const load = useCallback(async () => {
    try {
      const it = await api<Item>(`/archives/pieces/${id}`);
      setItem(it);
      setValues(
        Object.fromEntries(
          FIELDS.map((f) => [f.key, it.verified?.[f.key] ?? it.extraction?.[f.key]?.value ?? '']),
        ),
      );
    } catch (e) {
      setError(e as ApiError);
    }
  }, [id]);
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let url: string | null = null;
    const token = currentAccessToken();
    fetch(`${config.apiUrl}/archives/pieces/${id}/original`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) {
          url = URL.createObjectURL(blob);
          setPreview(url);
        }
      })
      .catch(() => setPreview(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);

  const act = async (path: string, body: unknown) => {
    setError(null);
    try {
      setItem(await api<Item>(`/archives/pieces/${id}/${path}`, { method: 'POST', body }));
      setReason('');
    } catch (e) {
      setError(e as ApiError);
    }
  };

  if (!item) return error ? <ErrorBox error={error} /> : <p>Chargement…</p>;
  const editable = item.status === 'a_verifier' && can('archives.verifier');

  return (
    <div className="stack">
      <div className="head">
        <div>
          <p className="crumb">
            <Link to={`/espace/archives/lots/${item.batch_id}`}>‹ Lot {item.batch_code}</Link>
          </p>
          <div className="arch-title">
            <h1>
              Pièce n° {item.sequence}{' '}
              <span className="muted">
                (p. {item.page_from}
                {item.page_to !== item.page_from ? `-${item.page_to}` : ''})
              </span>
            </h1>
            <Badge tone={ITEM_STATUS[item.status]?.tone ?? 'muted'}>
              {ITEM_STATUS[item.status]?.label ?? item.status}
            </Badge>
          </div>
        </div>
      </div>
      <ErrorBox error={error} />
      <div className="split">
        <section className="card wide arch-original" aria-labelledby="arch-orig">
          <h2 id="arch-orig">Original</h2>
          <div className="arch-viewer">
            {preview ? (
              item.mime === 'application/pdf' ? (
                <iframe title="Original numérisé" src={preview} />
              ) : (
                <img src={preview} alt="Original numérisé" />
              )
            ) : (
              <p className="muted">Aperçu indisponible.</p>
            )}
          </div>
          <p className="arch-print">
            {item.capture_device} - empreinte <span className="mono">{item.original_sha256}</span>
          </p>
        </section>
        <div className="card narrow flow arch-values">
          <h2>Valeurs</h2>
          <p className="alert alert-ic">
            <InfoIcon />
            <span>
              Proposition {item.extraction_model ?? '-'} : une valeur sous {Math.round(item.threshold * 100)}{' '}
              % de confiance est incertaine et impose un double contrôle. Laissez vide ce qui est illisible :
              rien n’est reconstruit.
            </span>
          </p>
          {FIELDS.map((f) => {
            const proposed = item.extraction?.[f.key];
            const uncertain = item.uncertainFields.includes(f.key);
            return (
              <div key={f.key} className={uncertain ? 'field arch-uncertain' : 'field'}>
                <label htmlFor={`f-${f.key}`}>
                  {f.label} {uncertain && <Badge tone="warn">incertain</Badge>}
                  {proposed && proposed.value !== null && (
                    <span className="proposed">
                      (proposé : « {proposed.value} », confiance {Math.round(proposed.confidence * 100)} %)
                    </span>
                  )}
                </label>
                <input
                  id={`f-${f.key}`}
                  disabled={!editable}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                />
                {f.hint && <span className="hint">{f.hint}</span>}
              </div>
            );
          })}
          {editable && (
            <>
              <fieldset className="arch-dest">
                <legend>Destination</legend>
                <label className="checkbox">
                  <input
                    type="radio"
                    name="outcome"
                    checked={outcome === 'inscription'}
                    onChange={() => setOutcome('inscription')}
                  />
                  Inscription au registre
                </label>
                <label className="checkbox">
                  <input
                    type="radio"
                    name="outcome"
                    checked={outcome === 'consultable'}
                    onChange={() => setOutcome('consultable')}
                  />
                  Archive consultable seulement
                </label>
              </fieldset>
              <button
                type="button"
                className="btn"
                onClick={() =>
                  void act('verifier', {
                    fields: Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v || null])),
                    outcome,
                  })
                }
              >
                Enregistrer la vérification
              </button>
            </>
          )}
        </div>
      </div>

      {item.status === 'double_controle' && can('archives.verifier') && (
        <div className="card flow">
          <h2>Double contrôle</h2>
          {item.verified_by === me?.userId ? (
            <p className="alert alert-ic">
              <InfoIcon />
              <span>Vous avez fait la première vérification : un autre vérificateur doit contrôler.</span>
            </p>
          ) : (
            <>
              <div className="field">
                <label htmlFor="dc-reason">Motif</label>
                <input id="dc-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
              <div className="actions arch-actions">
                <button
                  type="button"
                  className="btn"
                  disabled={reason.length < 5}
                  onClick={() => void act('double-controle', { approve: true, reason })}
                >
                  Confirmer les valeurs
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={reason.length < 5}
                  onClick={() => void act('double-controle', { approve: false, reason })}
                >
                  Retourner en vérification
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {item.status === 'soumis' && can('inscription.valider') && (
        <div className="card flow">
          <h2>Validation judiciaire</h2>
          <p className="muted">
            {item.outcome === 'consultable'
              ? 'Classée en archive consultable : aucune inscription ne sera créée.'
              : 'L’inscription sera créée au registre avec la provenance de l’archive ; le statut juridique reste « inconnu » tant qu’il n’est pas documenté.'}
          </p>
          {item.outcome === 'inscription' && (
            <div className="field">
              <label htmlFor="v-person">Personne existante (identifiant, facultatif)</label>
              <input id="v-person" value={personId} onChange={(e) => setPersonId(e.target.value.trim())} />
              <span className="hint">
                Recherchez d’abord au <Link to="/espace/casier-central">registre</Link> ; sans identifiant,
                une nouvelle fiche est créée.
              </span>
            </div>
          )}
          <div className="field">
            <label htmlFor="v-reason">Motif</label>
            <input id="v-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="actions arch-actions">
            <button
              type="button"
              className="btn"
              disabled={reason.length < 5}
              onClick={() =>
                void act('valider', { approve: true, reason, ...(personId ? { personId } : {}) })
              }
            >
              Valider
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={reason.length < 5}
              onClick={() => void act('valider', { approve: false, reason })}
            >
              Rejeter
            </button>
          </div>
        </div>
      )}

      {item.inscription_id && (
        <p className="alert alert-ok">
          Intégrée :{' '}
          <Link to={`/espace/registre/inscriptions/${item.inscription_id}`}>
            voir l’inscription au registre
          </Link>
          .
        </p>
      )}

      <section className="card flow" aria-labelledby="arch-hist">
        <h2 id="arch-hist">Historique</h2>
        <ol className="tl">
          {item.history.map((h, k) => (
            <li key={k} className={k === item.history.length - 1 ? 'now' : 'done'}>
              <b>
                {ITEM_STATUS[h.to_status]?.label ?? h.to_status} - {h.actor}
              </b>
              <span>{formatDate(h.occurred_at)}</span>
              {h.reason && <span className="arch-reason">{h.reason}</span>}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

/** File des pièces d'archives à valider (validateurs judiciaires, espace Juridictions). */
export function ArchivesToValidate() {
  const [rows, setRows] = useState<
    | {
        id: string;
        batch_code: string;
        sequence: number;
        page_from: number;
        outcome: string;
        verified: Record<string, string | null>;
      }[]
    | null
  >(null);
  useEffect(() => {
    api<NonNullable<typeof rows>>('/archives/pieces/a-valider')
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="card flow">
      <h2>Reprises d’archives à valider</h2>
      <ul className="arch-list">
        {rows.map((r) => (
          <li key={r.id}>
            <Link to={`/espace/archives/pieces/${r.id}`}>
              Lot {r.batch_code}, pièce {r.sequence}
            </Link>{' '}
            -{' '}
            {r.outcome === 'consultable'
              ? 'archive consultable'
              : `${r.verified.surname ?? '?'}, ${r.verified.decisionReference ?? '?'}`}
          </li>
        ))}
      </ul>
    </div>
  );
}
